import { type ReactNode, useMemo } from 'react'
import { LinkButton } from '@/Reservations/Search/ExperienceCard/LinkButton/LinkButton'
import { reservationWidgetMessages } from '@/reservationWidgetMessages'
import { useLocales } from '@sevenrooms/locales'
import styled from '@sevenrooms/ui-kit'
import { Box, HStack } from '@sevenrooms/ui-kit/layout'
import { ReservationTimeslots } from '../ReservationTimeslots'
import type { ReservationTimeSlotVariant } from '../../../../../components/ReservationTimeslot'
import type { AvailabilityTimeWithUpSellCost } from '../../../../../hooks'

interface RowPreviewTimeSlotsProps {
  timeslots?: AvailabilityTimeWithUpSellCost[]
  requestOrPriorityAlertAvailability?: ReactNode
  timeSlotVariant?: ReservationTimeSlotVariant
  onTimeSlotClick?: (slot: AvailabilityTimeWithUpSellCost) => void
  onShowMoreClick?: () => void
  descriptionType?: 'short' | 'full'
  currencyCode: string
}

export function RowPreviewTimeSlots({
  timeslots,
  requestOrPriorityAlertAvailability,
  timeSlotVariant = 'primary',
  descriptionType = 'full',
  onTimeSlotClick,
  onShowMoreClick,
  currencyCode,
}: RowPreviewTimeSlotsProps) {
  const { formatMessage } = useLocales()
  const timeSlotsCountToDisplay = requestOrPriorityAlertAvailability ? 1 : 2
  const showMoreButton = (timeslots?.length || 0) + (requestOrPriorityAlertAvailability ? 1 : 0) > 3
  const hiddenTimeSlotsCount = (timeslots?.length || 0) - timeSlotsCountToDisplay
  const timeSlotsToDisplay = useMemo(() => {
    if (showMoreButton) {
      return timeslots?.slice(0, timeSlotsCountToDisplay)
    }
    return timeslots
  }, [showMoreButton, timeSlotsCountToDisplay, timeslots])

  return (
    <StyledContainer
      data-test="sr-room-card-availability-container"
      width="100%"
      spacing="sm"
      justifyContent="flex-start"
      flexWrap="nowrap"
    >
      <ReservationTimeslots
        timeslotWidth="100%"
        currencyCode={currencyCode}
        timeslots={timeSlotsToDisplay}
        onTimeSlotClick={onTimeSlotClick}
        timeSlotVariant={timeSlotVariant}
        descriptionType={descriptionType}
      />
      {requestOrPriorityAlertAvailability && <Box>{requestOrPriorityAlertAvailability}</Box>}
      {showMoreButton && (
        <LinkButton
          text={formatMessage(reservationWidgetMessages.resWidgetPrivateEventsMoreTimeSlotsText, {
            quantity: hiddenTimeSlotsCount,
          })}
          onClick={onShowMoreClick}
          data-test="sr-room-card-show-more-timeslots-label-button"
        />
      )}
    </StyledContainer>
  )
}

const StyledContainer = styled(HStack)`
  > *:not(button) {
    flex: 1 1 0;
    min-width: 0;
  }
`
